import { useEffect, useState } from "react";
import { DateInput } from "@/components/DateInput";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Plus, Trash, CircleNotch, ArrowsClockwise, Power, CaretDown, CaretUp, Play } from "@phosphor-icons/react";

const FREQS = [
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
  { key: "yearly", label: "Yearly" },
];
const EMPTY = { client_name: "", client_email: "", description: "", rate: "", frequency: "monthly", start_date: "", status_mode: "draft" };

const fmtDate = (d) => { try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return d; } };

export function RecurringInvoices({ businessId, onGenerated }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get(`/businesses/${businessId}/recurring-invoices`).then((r) => { setItems(r.data); }).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [businessId]);

  const create = async () => {
    if (!form.client_name.trim()) return toast.error("Add a client name");
    if (!(parseFloat(form.rate) > 0)) return toast.error("Add an amount");
    setSaving(true);
    try {
      await api.post(`/businesses/${businessId}/recurring-invoices`, {
        client_name: form.client_name.trim(),
        client_email: form.client_email.trim(),
        items: [{ description: form.description.trim() || "Recurring service", quantity: 1, rate: parseFloat(form.rate) }],
        frequency: form.frequency,
        start_date: form.start_date || "",
        status_mode: form.status_mode,
      });
      toast.success("Recurring invoice scheduled");
      setForm(EMPTY);
      setShowForm(false);
      load();
      onGenerated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't schedule.");
    } finally { setSaving(false); }
  };

  const runNow = async (r) => {
    try {
      const { data } = await api.post(`/recurring-invoices/${r.id}/run`);
      toast.success(`Generated ${data.invoice_number} (${r.status_mode === "sent" ? "ready to share" : "draft"})`);
      load();
      onGenerated?.();
    } catch { toast.error("Couldn't generate."); }
  };

  const toggle = async (r) => {
    try { const { data } = await api.patch(`/recurring-invoices/${r.id}`, { active: !r.active }); setItems((xs) => xs.map((x) => (x.id === r.id ? data : x))); } catch { toast.error("Couldn't update."); }
  };

  const del = async (id) => {
    try { await api.delete(`/recurring-invoices/${id}`); setItems((x) => x.filter((r) => r.id !== id)); } catch {}
  };

  return (
    <div className="bk-card overflow-hidden" data-testid="recurring-panel">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-6 py-4" data-testid="recurring-toggle">
        <div className="flex items-center gap-2.5">
          <ArrowsClockwise size={20} weight="duotone" color="var(--bk-accent)" />
          <span className="font-display font-bold" style={{ color: "var(--bk-text)" }}>Recurring invoices</span>
          {items.length > 0 && <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: "var(--bk-accent-glow)", color: "var(--bk-accent)" }}>{items.length}</span>}
        </div>
        {open ? <CaretUp size={18} weight="bold" color="var(--bk-text-2)" /> : <CaretDown size={18} weight="bold" color="var(--bk-text-2)" />}
      </button>

      {open && (
        <div className="px-6 pb-6 space-y-4 border-t" style={{ borderColor: "var(--bk-border)" }}>
          <div className="flex items-center justify-between pt-4">
            <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>Auto-generate invoices on a schedule. New ones land as <strong>drafts</strong> for you to review, then share.</p>
            <button onClick={() => setShowForm((s) => !s)} data-testid="new-recurring-btn" className="bk-btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold shrink-0">
              <Plus size={15} weight="bold" /> Schedule
            </button>
          </div>

          {showForm && (
            <div className="rounded-lg p-4 space-y-3" style={{ background: "var(--bk-page)" }} data-testid="recurring-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input placeholder="Client name" value={form.client_name} onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))} data-testid="recurring-client" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
                <input placeholder="Client email (optional)" value={form.client_email} onChange={(e) => setForm((f) => ({ ...f, client_email: e.target.value }))} data-testid="recurring-email" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
                <input placeholder="Line item description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="recurring-description" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
                <input type="number" min="0" step="0.01" placeholder="Amount (USD)" value={form.rate} onChange={(e) => setForm((f) => ({ ...f, rate: e.target.value }))} data-testid="recurring-amount" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
                <select value={form.frequency} onChange={(e) => setForm((f) => ({ ...f, frequency: e.target.value }))} data-testid="recurring-frequency" className="rounded-md border px-3 py-2 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)" }}>
                  {FREQS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                </select>
                <DateInput value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} data-testid="recurring-start" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
              </div>
              <label className="flex items-center gap-2 text-sm" style={{ color: "var(--bk-text-2)" }}>
                <input type="checkbox" checked={form.status_mode === "sent"} onChange={(e) => setForm((f) => ({ ...f, status_mode: e.target.checked ? "sent" : "draft" }))} data-testid="recurring-autosent" />
                Create each invoice ready-to-share (skip draft review)
              </label>
              <div className="flex justify-end">
                <button onClick={create} disabled={saving} data-testid="save-recurring-btn" className="bk-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-70">
                  {saving ? <><CircleNotch size={16} className="animate-spin" /> Scheduling…</> : <>Schedule</>}
                </button>
              </div>
            </div>
          )}

          {items.length > 0 && (
            <div className="space-y-2">
              {items.map((r) => (
                <div key={r.id} data-testid={`recurring-${r.id}`} className="flex items-center justify-between gap-3 rounded-lg border px-4 py-3" style={{ borderColor: "var(--bk-border)", opacity: r.active ? 1 : 0.6 }}>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate" style={{ color: "var(--bk-text)" }}>{r.client_name} · <span className="font-mono">${Number(r.total).toFixed(2)}</span></p>
                    <p className="text-xs mt-0.5" style={{ color: "var(--bk-text-2)" }}>
                      {FREQS.find((f) => f.key === r.frequency)?.label} · next {fmtDate(r.next_run)} · {r.status_mode === "sent" ? "auto-share" : "draft"} · {r.active ? "active" : "paused"}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => runNow(r)} data-testid={`recurring-run-${r.id}`} className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-full border hover:bg-slate-50" style={{ borderColor: "var(--bk-border)", color: "var(--bk-accent)" }}><Play size={13} weight="fill" /> Run now</button>
                    <button onClick={() => toggle(r)} data-testid={`recurring-toggle-${r.id}`} className="p-1.5 rounded hover:bg-black/5" style={{ color: "var(--bk-text-2)" }} aria-label="Pause/Resume"><Power size={15} weight="bold" /></button>
                    <button onClick={() => del(r.id)} data-testid={`recurring-delete-${r.id}`} className="p-1.5 rounded hover:bg-black/5" style={{ color: "var(--bk-energy)" }} aria-label="Delete"><Trash size={15} weight="bold" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
